import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Form } from "react-bootstrap";
import { CardText, CardTitle, Col, Container, Row } from "reactstrap";
import ControlledTextField from "../../../components/ControlledTextField";
import logo from "../../../assets/icons/logo1.PNG";
import GoogleIcon from "../../../assets/icons/google-icon.PNG";
import "./index.css";
import PhoneInput from "react-phone-input-2";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithPopup,
} from "firebase/auth";
import { toast } from "react-toastify";
import { auth, generateToken } from "../../../firebase";
import { useCreateCustomerMutation } from "../../../redux/services/customerCreateService";
import GoogleSignUpData from "./GoogleSignUpData";
import { useLazyGetExistingUserInfoQuery } from "../../../redux/services/getUserService";
import { useLazyGetCustomerProfileQuery } from "../../../redux/services/getCustomer";
import { useAddMessageTokenMutation } from "../../../redux/services/userService";
import { trackSignUp, trackLogin } from "../../../utility/analyticsUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

type FormData = {
  email: string;
  password: string;
  confirmPassword: string;
  countryCode?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
};

interface SignUpModalProps {
  setModalOpen: any;
  setIsSignInModal: any;
}

const SignUpModal: React.FC<SignUpModalProps> = ({
  setModalOpen,
  setIsSignInModal,
}) => {
  const navigate = useNavigate();

  const { control, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    mode: 'onChange' // Enable real-time validation
  });
  const [createCustomer] = useCreateCustomerMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDataModalOpen, setIsDataModalOpen] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>({
    displayName: "",
    email: "",
    phoneNumber: "",
    photoUrl: "",
  });
  const [dots, setDots] = useState("");

  const [triggerGetUserInfo] = useLazyGetExistingUserInfoQuery();
  const [triggerGetCustomerInfo] = useLazyGetCustomerProfileQuery();
  const [
    addMessageToken,
  ] = useAddMessageTokenMutation();

  const validateAndFormatPhoneNumber = (phone: string): string | null => {
    // Remove spaces and unnecessary characters
    const cleanedPhone = phone.replace(/\D/g, ""); // Remove non-numeric characters

    if (/^94\d{9}$/.test(cleanedPhone)) {
      return cleanedPhone.substring(2); // Remove '94' and return last 9 digits
    }

    if (/^0\d{9}$/.test(cleanedPhone)) {
      return cleanedPhone.substring(1); // Remove '0' and return last 9 digits
    }

    return null; // Invalid number
  };

  const GetCustomerData = async (accessToken: string) => {
    const response = await triggerGetCustomerInfo(accessToken);
    if (response.data?.id) {
      localStorage.setItem("customerId", response.data.id);
      sendMessageToken();
    }
  };

  const sendMessageToken = async () => {
    const token = await generateToken();
    if (token) {
      const newCustomerId = localStorage.getItem("customerId") || "";
      if (newCustomerId !== "") {
        await addMessageToken({
          token,
          userType: "WebCustomer",
          customerId: newCustomerId,
        });
      }
    }
  };

  //  please change this one wht you wont to pass
  const signUp = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phoneNumber?: string
  ) => {
    try {
      setIsLoading(true);
      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      // console.log("User created with UID: ", userCredential);

      if (userCredential.user) {
        await sendEmailVerification(userCredential.user);
        toast.success("Verification email sent. Please check your inbox.");
      }

      // Construct displayName
      const displayName = `${firstName || ""} ${lastName || ""}`.trim();

      // Call createCustomer API after Firebase user is created
      await createCustomer({
        userId: userCredential.user.uid,
        displayName: displayName || "New User",
        firstName: firstName || "",
        lastName: lastName || "",
        email: email,
        phoneNumber: phoneNumber || ""
      }).unwrap();

      // Analytics: sign up via email
      try {
        trackSignUp('email', userCredential.user.uid);
      } catch (e) { }

      toast.success("Account Created Successfully! Please verify your email.");
      setIsSignInModal(true);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.error("Error during sign-up process: ", error);
      // console.log("Error code: ", error);
      if(error.code === 'auth/weak-password'){
        toast.error("The password is too weak. Please choose a stronger password.");
        return;
      }
      if(error.code === 'auth/email-already-in-use'){
        toast.error("The email address is already in use by another account.");
        return;
      }
      toast.error(
        error.code || error.data ||
        "Something went wrong, please check your email and password."
      );
    }
  };

  const googleSignUp = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const userCredential = await signInWithPopup(auth, provider);
      toast.success("Google Sign-In Successful!");
      const accessToken = await userCredential.user.getIdToken();
      localStorage.setItem("accessToken", accessToken);

      if (userCredential.user.uid) {
        localStorage.setItem("ownerID", userCredential.user.uid);
      }
      if (userCredential.user.email) {
        localStorage.setItem("email", userCredential.user.email);
      }
      if (userCredential.user.photoURL) {
        localStorage.setItem("photoURL", userCredential.user.photoURL);
      }

      const response = await triggerGetUserInfo(accessToken);

      if (response?.data?.userRoles?.includes("customer")) {
        toast.success("Welcome back!");
        setModalOpen(false);
        GetCustomerData(accessToken);
        // Analytics: google login from sign up screen
        try { trackLogin('google', response.data.userId); } catch (e) { }
      } else {
        toast.info("New user detected, please complete signup.");
        setIsDataModalOpen(true);
        const userDataObj: any = {
          userId: userCredential.user.uid,
          displayName: userCredential.user.displayName || "New User",
          email: userCredential.user.email,
          phoneNumber: userCredential.user.phoneNumber
        };

        setUserData(userDataObj);
        setIsDataModalOpen(true);
        // Analytics: google sign up detected
        try { trackSignUp('google', userCredential.user.uid); } catch (e) { }
      }

      GetCustomerData(accessToken);

      localStorage.setItem("isUserSignIN", "true");
      navigate("/");
    } catch (error: any) {
      console.error("Error signing in with Google: ", error);
      toast.error(error.message || "Failed to sign in with Google.");
    }
  };

  const onSubmit = (data: FormData) => {
    // Check if passwords match
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match. Please try again.");
      return;
    }

    // Check password strength
    if (data.password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    const formattedPhoneNumber = validateAndFormatPhoneNumber(
      data.phoneNumber || ""
    );

    if (!formattedPhoneNumber) {
      toast.error(
        "Invalid phone number. Use +947XXXXXXXX or 07XXXXXXXX format."
      );
      return;
    }

    signUp(
      data.email,
      data.password,
      data.firstName,
      data.lastName,
      formattedPhoneNumber
    );
  };

  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);
    return () => clearInterval(interval);
  }, [isLoading]);

  return (
    <>
      <div>
        <Container
          fluid
          className="d-flex align-items-center justify-content-center auth-wrapper auth-cover"
        >
          <div
            className="p-4 card-signin  text-center"
            style={{ maxWidth: "450px", width: "100%" }}
          >
            <div className="my-1">
              <img
                // src={`${IMAGE_BASE_URL}assets/images/logo.PNG`}
                src={logo}
                alt="loginimage"
                style={{ width: "200px" }}
              />
            </div>

            {isDataModalOpen ? (
              <GoogleSignUpData
                userData={userData}
                setUserData={setUserData}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                setIsDataModalOpen={setIsDataModalOpen}
                setModalOpen={setModalOpen}
              />
            ) : (
              <>
                <CardTitle tag="h2" className="fw-bold text-light mb-1">
                  Welcome to Curry King!
                </CardTitle>
                <CardText className="mb-1 text-light">
                  Please sign-up to your account and start the adventure
                </CardText>
                <Form
                  className="auth-login-form mt-1"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Row className="mt-1 justify-content-space-between">
                    <Col className="p-0 me-2">
                      <ControlledTextField
                        label=""
                        inputName="firstName"
                        inputType="text"
                        placeholder="First Name"
                        isRequired={true}
                        control={control}
                        rules={{
                          required: "First name is required",
                          minLength: {
                            value: 2,
                            message: "First name must be at least 2 characters"
                          }
                        }}
                      />
                      {errors.firstName && (
                        <div className="text-danger small mt-1">
                          {errors.firstName.message}
                        </div>
                      )}
                    </Col>
                    <Col md="6" className="p-0">
                      <ControlledTextField
                        label=""
                        inputName="lastName"
                        inputType="text"
                        placeholder="Last Name"
                        isRequired={true}
                        control={control}
                        rules={{
                          required: "Last name is required",
                          minLength: {
                            value: 2,
                            message: "Last name must be at least 2 characters"
                          }
                        }}
                      />
                      {errors.lastName && (
                        <div className="text-danger small mt-1">
                          {errors.lastName.message}
                        </div>
                      )}
                    </Col>
                  </Row>
                  <div className="mb-1">
                    <ControlledTextField
                      label={""}
                      inputName={"email"}
                      inputType={"email"}
                      placeholder={"Email"}
                      isRequired={true}
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Please enter a valid email address"
                        }
                      }}
                    />
                    {errors.email && (
                      <div className="text-danger small mt-1">
                        {errors.email.message}
                      </div>
                    )}
                  </div>
                  <div className="mb-1">
                    <ControlledTextField
                      label={""}
                      inputName={"password"}
                      isRequired={true}
                      placeholder={"Password"}
                      inputType={"password"}
                      control={control}
                      rules={{ 
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters long"
                        }
                      }}
                    />
                    {errors.password && (
                      <div className="text-danger small mt-1">
                        {errors.password.message}
                      </div>
                    )}
                  </div>
                  <div className="mb-1">
                    <ControlledTextField
                      label={""}
                      inputName={"confirmPassword"}
                      isRequired={true}
                      placeholder={"Confirm Password"}
                      inputType={"password"}
                      control={control}
                      rules={{
                        required: "Please confirm your password",
                        validate: (value: string) =>
                          value === watch("password") || "Passwords do not match"
                      }}
                    />
                    {errors.confirmPassword && (
                      <div className="text-danger small mt-1">
                        {errors.confirmPassword.message}
                      </div>
                    )}
                  </div>
                  <div className="mb-2 mt-4">
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <PhoneInput
                          {...field}
                          country={"lk"}
                          inputClass="form-control"
                          containerClass="mb-1"
                          inputProps={{
                            name: "phoneNumber",
                            required: true,
                            autoFocus: true,
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="d-grid">
                    {/* <button
                      type="submit"
                      className="btn my-2 py-2 btn-signin-color btn-block"
                    >
                      Sign Up
                    </button> */}
                    <button
                      type="submit"
                      className="btn my-2 py-2 btn-signin-color btn-block position-relative"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                          Signing Up
                          <span className="dots-container">{dots}</span>
                        </>
                      ) : (
                        "Sign Up"
                      )}
                    </button>
                  </div>

                  <div className="text-center text-light mb-1">
                    <p>
                      Already have an account? Please{" "}
                      <div
                        className="btn mx-2 text-link-color"
                        onClick={() => {
                          setIsSignInModal(true);
                        }}
                        role="button"
                      >
                        SignIn
                      </div>
                    </p>
                  </div>

                  <div className="d-grid mb-2">
                    <button
                      className="btn text-link-color d-flex align-items-center justify-content-center"
                      onClick={googleSignUp}
                    >
                      <img
                        src={GoogleIcon}
                        alt="Google Icon"
                        style={{ width: "20px", marginRight: "10px" }}
                      />
                      Sign Up with Google
                    </button>
                  </div>
                </Form>
              </>
            )}
          </div>
        </Container>
      </div>
    </>
  );
};

export default SignUpModal;
